// The create wizard (web/templates/web/create.html), BOOK agent; bundled into app.js.
//
// Nine steps, one question each, in ONE real form - the
// browser checks the required fields, we only show the step a missing field
// is in. Picking a tile moves on by itself. Everything typed or picked is kept
// in localStorage (photos excepted - a browser cannot store a file for a
// form), so a reload or a closed tab lands where the visitor left off.
//
// A photo is cropped to a portrait (head + shoulders, 3:4) right after it is picked (crop.js) - only the
// crop is uploaded. The card shows the whole photo with the chosen frame on it (photopick.js); a tap on it
// opens the crop again (from the original, kept in the browser's memory only).
// "Deine Auswahl" pills under the progress bar show what was picked and jump back to that step; the last
// step sums everything up.
const {cropPhoto} = require('./crop');
const {photoProblem, previewHtml} = require('./photopick');

const STORE = 'mh-create-v6';   // v6: no size choice (32 pages for every book); v5: Vorwort after Figuren   // bump whenever the steps change: a stored step number would point elsewhere

function load() {
  try { return JSON.parse(localStorage.getItem(STORE) || 'null'); } catch (e) { return null; }
}
function store(data) {
  try { localStorage.setItem(STORE, JSON.stringify(data)); } catch (e) { /* private mode: no memory */ }
}
function forget() {
  try { localStorage.removeItem(STORE); } catch (e) { /* nothing to forget */ }
}

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));

function initWizard(form) {
  const steps = [...form.querySelectorAll('[data-step]')];
  const back = form.querySelector('[data-back]');
  const next = form.querySelector('[data-next]');
  const submit = form.querySelector('[data-submit]');
  const fill = document.querySelector('[data-progress-fill]');
  const bar = document.querySelector('[data-progress]');
  const topBlock = document.querySelector('[data-wizard-top]');
  // we place the step ourselves (anchor()); the browser restoring an old scroll position on back would fight it
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  let current = 0;
  let restoring = false;

  // --- steps ------------------------------------------------------------------
  function hasAnswer(step) {
    return [...step.querySelectorAll('input[type=radio][name], input[type=text][name], input:not([type])[name], textarea[name]')]
      .some(f => (f.type === 'radio' ? f.checked : f.value.trim()));
  }

  function refreshNav() {
    const step = steps[current];
    back.style.visibility = current === 0 ? 'hidden' : 'visible';
    next.hidden = current === steps.length - 1;
    submit.hidden = current !== steps.length - 1;
    next.textContent = step.hasAttribute('data-optional') && !hasAnswer(step) ? 'Überspringen →' : 'Weiter →';
  }

  // Every step is a history entry (#schritt-3): the browser's back - a swipe, the mouse
  // button, the arrow - goes to the previous step instead of leaving the page (Dennis).
  function show(index, direction = 0, fromHistory = false) {
    index = Math.max(0, Math.min(steps.length - 1, index));
    if (direction && !fromHistory && index !== current) history.pushState({step: index}, '', `#schritt-${index + 1}`);
    current = index;
    steps.forEach((s, i) => {
      s.hidden = i !== index;
      s.classList.remove('is-entering-next', 'is-entering-back');
    });
    const step = steps[index];
    if (direction) {
      void step.offsetWidth;                                  // restart the animation
      step.classList.add(direction > 0 ? 'is-entering-next' : 'is-entering-back');
    }
    // never a step number (Dennis: "das demotiviert") - only the bar, and its value for screen readers
    const percent = Math.round(((index + 1) / steps.length) * 100);
    fill.style.setProperty('--progress', `${percent}%`);
    if (bar) { bar.setAttribute('aria-valuenow', percent); bar.setAttribute('aria-valuetext', step.dataset.label); }
    if (step.dataset.label === 'Motiv') applyTheme();
    if (index === steps.length - 1) { summary(); refreshTotal(); }
    refreshPicks();
    refreshNav();
    save();
    if (!restoring) anchor();
  }

  // Every step starts at the same place: the progress bar right under the sticky header. An instant
  // jump (never smooth): the step slides in anyway, and a smooth scroll during that landed "wrong" (Dennis).
  function anchor() {
    const header = document.querySelector('.site-header');
    const offset = (header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0) + 8;
    const y = topBlock ? topBlock.getBoundingClientRect().top + window.scrollY - offset : 0;
    window.scrollTo({top: y < 24 ? 0 : y, behavior: 'instant'});
  }

  // the browser's own bubbles speak the browser's language - ours always speak German (QA 2026-10-13)
  function german(f) {
    const v = f.validity;
    if (v.valueMissing) {
      if (f.type === 'radio') return f.name.endsWith('-gender') ? 'Bitte wähle eine Möglichkeit aus.' : 'Bitte wähle eine Kachel aus.';
      if (f.type === 'checkbox') return 'Bitte setze hier das Häkchen.';
      if (f.type === 'file') return 'Bitte wähle ein Foto aus.';
      if (f.name.endsWith('-name')) return 'Bitte gib einen Namen ein.';
      if (f.name.endsWith('-age')) return 'Bitte gib das Alter ein.';
      return 'Bitte fülle dieses Feld aus.';
    }
    if (v.rangeOverflow) return `Bitte höchstens ${f.max} eingeben.`;
    if (v.rangeUnderflow) return `Bitte mindestens ${f.min} eingeben.`;
    if (v.badInput || v.stepMismatch) return 'Bitte gib eine ganze Zahl ein.';
    if (v.tooLong) return `Bitte höchstens ${f.maxLength} Zeichen.`;
    return '';
  }
  const clearGerman = (f) => { if (f.setCustomValidity) f.setCustomValidity(''); };
  form.addEventListener('input', (e) => clearGerman(e.target), true);
  form.addEventListener('change', (e) => {
    clearGerman(e.target);
    if (e.target.type === 'radio') form.querySelectorAll(`[name="${CSS.escape(e.target.name)}"]`).forEach(clearGerman);
  }, true);

  function stepValid(index) {
    const fields = [...steps[index].querySelectorAll('input, select, textarea')].filter(f => !f.disabled);
    fields.forEach(clearGerman);
    const invalid = fields.find(f => !f.checkValidity());
    if (invalid) {
      form.classList.add('is-checked');
      invalid.reportValidity();
      return false;
    }
    return true;
  }

  next.addEventListener('click', () => { if (stepValid(current)) show(current + 1, 1); });
  // our own back button walks the same history, so both backs stay one thing
  back.addEventListener('click', () => { if (current > 0) history.back(); });
  window.addEventListener('popstate', (e) => {
    const step = e.state && typeof e.state.step === 'number' ? e.state.step : 0;
    if (step !== current) show(step, step < current ? -1 : 1, true);
  });
  // A missing field in a hidden step: go there, so the browser can point at it.
  form.addEventListener('invalid', (e) => {
    if (!e.target.validationMessage || e.target.validity.customError === false) {
      const message = german(e.target);
      if (message) e.target.setCustomValidity(message);
    }
    form.classList.add('is-checked');
    const index = steps.indexOf(e.target.closest('[data-step]'));
    if (index >= 0 && index !== current) show(index, index > current ? 1 : -1);
  }, true);
  // before the browser checks the whole form: no stale message from an earlier check may block it
  submit.addEventListener('click', () => form.querySelectorAll('input, textarea').forEach(clearGerman));
  form.addEventListener('submit', () => {
    submit.disabled = true;
    submit.textContent = 'Wird gezaubert …';
    forget();
  });
  // Enter in a text field goes on instead of sending the whole form half-filled
  form.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.matches('input:not([type=radio]):not([type=checkbox])') && current < steps.length - 1) {
      e.preventDefault();
      next.click();
    }
  });

  // A tile moves on by itself (after a beat, so the choice is seen), and clears the own words.
  form.querySelectorAll('[data-auto-next]').forEach(input => {
    input.addEventListener('change', () => {
      const own = steps[current].querySelector('[data-own-words]');
      if (own) own.value = '';
      refreshNav();
      save();
      const from = current;
      setTimeout(() => { if (current === from && stepValid(current)) show(current + 1, 1); }, 520);
    });
  });
  // Own words replace the tile.
  form.querySelectorAll('[data-own-words]').forEach(input => {
    input.addEventListener('input', () => {
      if (input.value.trim()) steps[current].querySelectorAll('input[type=radio]').forEach(r => { r.checked = false; });
      refreshNav();
    });
  });

  // --- characters -----------------------------------------------------------
  const cards = [...form.querySelectorAll('[data-character]')];
  const add = form.querySelector('[data-add-character]');


  function refreshAdd() {
    add.hidden = cards.every(c => !c.hidden);
  }

  function applyKind(card) {
    const checked = card.querySelector('[data-kind]:checked');
    const kind = checked ? checked.value : 'person';
    card.querySelectorAll('[data-person-only]').forEach(el => {
      el.hidden = kind !== 'person';
      el.querySelectorAll('input').forEach(i => { i.disabled = kind !== 'person' || card.disabled; });
    });
    const labelEl = card.querySelector('[data-description-label]');
    const input = card.querySelector('[data-description]');
    labelEl.textContent = labelEl.dataset[kind];
    input.placeholder = input.dataset[kind];
    input.required = kind !== 'person';
  }

  function openCard(card) {
    card.hidden = false;
    card.disabled = false;
    applyKind(card);
    refreshAdd();
  }

  cards.forEach(card => {
    card.querySelectorAll('[data-kind]').forEach(r => r.addEventListener('change', () => applyKind(card)));
    const remove = card.querySelector('[data-remove-character]');
    if (remove) {
      remove.addEventListener('click', () => {
        card.hidden = true;
        card.disabled = true;
        card.querySelectorAll('input:not([type=radio]):not([type=hidden])').forEach(i => { i.value = ''; });
        card.querySelectorAll('input[type=radio]').forEach(r => { r.checked = r.value === 'person' || r.value === 'm'; });   // the defaults: person, männlich
        clearPhoto(card);
        refreshAdd();
        refreshConsent();
        save();
      });
    }
    applyKind(card);
  });

  add.addEventListener('click', () => {
    const card = cards.find(c => c.hidden);
    if (!card) return;
    openCard(card);
    card.scrollIntoView({behavior: 'smooth', block: 'center'});
    card.querySelector('input[name$="-name"]').focus({preventScroll: true});
    save();
  });

  // --- photos: tips first, then the portrait crop; only the crop goes into the form -----
  const consent = form.querySelector('[data-consent]');
  const tips = document.querySelector('[data-tips-dialog]');
  const originals = new Map();          // input -> the original file (kept in the browser for "Neu zuschneiden")
  let tipsSeen = false;
  let tipsFor = null;

  function refreshConsent() {
    const any = [...form.querySelectorAll('[data-photo]')].some(i => !i.disabled && i.files && i.files.length);
    consent.hidden = !any;
    consent.querySelector('input').required = any;
  }

  function setFile(input, file) {
    const dt = new DataTransfer();
    if (file) dt.items.add(file);
    input.files = dt.files;
  }

  // the whole photo with the chosen frame (photopick.js); without the original (a restored form): the crop.
  // The crop's own URL (data-crop-url) is what the summary step shows.
  function preview(drop) {
    const input = drop.querySelector('[data-photo]');
    const card = drop.closest('[data-character]');
    let box = drop.querySelector('[data-photo-preview]');
    const file = input.files && input.files[0];
    if (box && box.dataset.cropUrl) URL.revokeObjectURL(box.dataset.cropUrl);
    if (file) {
      if (!box) {
        box = document.createElement('span');
        box.dataset.photoPreview = '';
        box.className = 'photo-preview';
        drop.appendChild(box);
      }
      box.dataset.cropUrl = URL.createObjectURL(file);
      const framed = originals.get(input) && input._crop && input._crop.frame ? input._crop : null;
      box.innerHTML = previewHtml(framed, box.dataset.cropUrl)
        + (framed ? '<span class="photo-edit-badge">✂️ Ausschnitt ändern</span>' : '');
    } else if (box) {
      box.remove();
    }
    drop.classList.toggle('has-photo', !!file);
    drop.classList.toggle('can-recrop', !!(file && originals.get(input)));
    const later = card.querySelector('.photo-later');
    if (later) later.hidden = !!file;
    card.querySelector('[data-photo-other]').hidden = !file;
    card.querySelector('[data-photo-remove]').hidden = !file;
  }

  function clearPhoto(card) {
    const input = card.querySelector('[data-photo]');
    if (input._crop && input._crop.sourceUrl) URL.revokeObjectURL(input._crop.sourceUrl);
    setFile(input, null);
    input._crop = null;
    originals.delete(input);
    preview(card.querySelector('[data-photo-drop]'));
  }

  // a friendly line under the photo when the file is not a usable picture (QA: a PDF failed silently)
  function photoError(input, message) {
    const card = input.closest('[data-character]');
    let note = card.querySelector('[data-photo-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'photo-error';
      note.dataset.photoError = '';
      note.setAttribute('role', 'alert');
      card.querySelector('[data-photo-drop]').after(note);
    }
    note.textContent = message;
    note.hidden = !message;
  }

  // again = the same original once more ("Neu zuschneiden", a tap on the preview): start at the last frame
  async function take(input, file, again = false) {
    if (!file) return;
    photoError(input, '');
    const problem = photoProblem(file);
    if (problem) {
      setFile(input, input._crop || null);
      photoError(input, problem);
      return;
    }
    let cropped = await cropPhoto(file, {frame: again && input._crop ? input._crop.frame : null});
    if (cropped && cropped.other) {         // "Anderes Foto" in the crop: the file picker (setFile keeps the old crop)
      setFile(input, input._crop || null);
      input.click();
      return;
    }
    if (cropped && cropped.error) {
      photoError(input, cropped.error);
      cropped = null;
    }
    if (cropped) {
      if (input._crop && input._crop.sourceUrl && input._crop.sourceUrl !== cropped.sourceUrl) URL.revokeObjectURL(input._crop.sourceUrl);
      originals.set(input, file);
      setFile(input, cropped);
      input._crop = cropped;                // the File with .frame/.source (input.files may hold a copy)
    } else if (!originals.get(input)) {
      setFile(input, null);                 // cancelled: nothing (never the uncropped photo)
      input._crop = null;
    } else {
      setFile(input, input._crop || null);
    }
    preview(input.closest('[data-photo-drop]'));
    refreshConsent();
    save();
  }

  form.querySelectorAll('[data-photo-drop]').forEach(drop => {
    const input = drop.querySelector('[data-photo]');
    const card = drop.closest('[data-character]');
    // the first time: the four photo tips, then the file picker
    drop.addEventListener('click', (e) => {
      // a photo is there: a tap on its preview opens the crop again (another photo: "Anderes Foto")
      if (input._crop && originals.get(input)) {
        e.preventDefault();
        take(input, originals.get(input), true);
        return;
      }
      if (tipsSeen || !tips) return;
      e.preventDefault();
      tipsFor = input;
      tips.showModal();
    });
    input.addEventListener('change', () => {
      const file = input.files[0];
      if (file && !(input._crop && file.name === input._crop.name && file.size === input._crop.size)) take(input, file);
    });
    ['dragenter', 'dragover'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.add('is-over'); }));
    ['dragleave', 'drop'].forEach(t => drop.addEventListener(t, e => { e.preventDefault(); drop.classList.remove('is-over'); }));
    drop.addEventListener('drop', e => { if (e.dataTransfer.files[0]) take(input, e.dataTransfer.files[0]); });
    card.querySelector('[data-photo-tips]').addEventListener('click', () => { tipsFor = input; tips.showModal(); });
    card.querySelector('[data-photo-other]').addEventListener('click', () => input.click());
    card.querySelector('[data-photo-remove]').addEventListener('click', () => { clearPhoto(card); refreshConsent(); });
  });
  if (tips) {
    tips.querySelector('[data-tips-close]').addEventListener('click', () => { tipsSeen = true; tips.close(); });
    tips.querySelector('[data-tips-go]').addEventListener('click', () => {
      tipsSeen = true;
      tips.close();
      if (tipsFor) tipsFor.click();
    });
  }

  // --- who a side character is: chips fill the field (and pick the kind: Haustier -> Tier) -------
  cards.forEach(card => {
    const field = card.querySelector('[data-relation-input]');
    if (!field) return;
    const mark = () => card.querySelectorAll('[data-relation]').forEach(c => c.classList.toggle('is-on', c.dataset.relation === field.value.trim()));
    card.querySelectorAll('[data-relation]').forEach(chip => chip.addEventListener('click', () => {
      field.value = chip.dataset.relation;
      // Oma, Opa, Mama, Papa: an empty name becomes the same word, selected - easy to keep or to overwrite
      const nameField = card.querySelector('input[name$="-name"]');
      if (['Mama', 'Papa', 'Oma', 'Opa'].includes(chip.dataset.relation) && !nameField.value.trim()) {
        nameField.value = chip.dataset.relation;
        nameField.focus({preventScroll: true});
        nameField.select();
      }
      const kind = card.querySelector(`[data-kind][value="${chip.dataset.relationKind}"]`);
      if (kind && !kind.checked) { kind.checked = true; applyKind(card); }
      if (chip.dataset.relationKind === 'person') {
        const gender = {Mama: 'f', Oma: 'f', Schwester: 'f', Freundin: 'f', Papa: 'm', Opa: 'm', Bruder: 'm', Freund: 'm'}[chip.dataset.relation];
        const radio = gender && card.querySelector(`input[name$="-gender"][value="${gender}"]`);
        if (radio) radio.checked = true;
      }
      mark();
      save();
    }));
    field.addEventListener('input', mark);
    card._markRelation = mark;
  });

  // --- the foreword helper: four answers -> three suggestions (Claude, cheap) ---------------
  const helperButton = form.querySelector('[data-foreword-suggest]');
  if (helperButton) {
    const list = form.querySelector('[data-foreword-suggestions]');
    helperButton.addEventListener('click', async () => {
      // "Von wem?" is the field above; the hero and who else is in the book come from the character cards
      const answers = {from: form.querySelector('[data-foreword-by]').value};
      form.querySelectorAll('[data-helper]').forEach(i => { answers[i.dataset.helper] = i.value; });
      const others = cards.slice(1).filter(c => !c.hidden).map(c => {
        const n = c.querySelector('input[name$="-name"]').value.trim();
        const r = c.querySelector('[data-relation-input]').value.trim();
        return n ? (r ? `${n} (${r})` : n) : '';
      }).filter(Boolean);
      const heroAge = form.querySelector('[name="c0-age"]').value;
      answers.book = [heroAge ? `${form.querySelector('[name="c0-name"]').value} ist ${heroAge} Jahre alt` : '', others.length ? `außerdem im Buch: ${others.join(', ')}` : ''].filter(Boolean).join('; ');
      if (![answers.from, answers.who, answers.occasion, answers.message].some(v => v && v.trim())) { form.querySelector('[data-helper]').focus(); return; }
      const before = helperButton.textContent;
      helperButton.disabled = true;
      helperButton.textContent = 'Denke nach …';
      let data = {};
      try {
        const response = await fetch(helperButton.dataset.url, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'X-CSRFToken': form.querySelector('[name=csrfmiddlewaretoken]').value},
          body: JSON.stringify({answers, hero: form.querySelector('[name="c0-name"]').value}),
        });
        data = await response.json().catch(() => ({}));
      } catch (e) { data = {message: 'Keine Verbindung – bitte gleich noch einmal versuchen.'}; }
      helperButton.disabled = false;
      helperButton.textContent = before;
      list.innerHTML = '';
      if (data.message) { list.textContent = data.message; return; }
      (data.suggestions || []).forEach(text => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'bk-suggestion';
        b.textContent = text;
        const tag = document.createElement('span');
        tag.textContent = 'Übernehmen';
        b.appendChild(tag);
        b.addEventListener('click', () => {
          form.querySelector('[data-foreword-text]').value = text;
          const by = form.querySelector('[data-foreword-by]');
          refreshNav();
          save();
        });
        list.appendChild(b);
      });
    });
  }

  // --- "Deine Auswahl" and the summary -----------------------------------------------------
  const picks = document.querySelector('[data-picks]');
  function chosen() {
    const items = [];
    const tile = (name, step, fallback = '') => {
      const input = form.querySelector(`input[name="${name}"]:checked`);
      if (!input) return;
      const label = input.closest('label');
      const icon = fallback ? null : label.querySelector('.choice-emoji, img');
      const text = (label.querySelector('.choice-name') || label.querySelector('.font-display') || label).textContent.trim();
      items.push({step, text: input.dataset.pickLabel || text, icon: icon ? (icon.tagName === 'IMG' ? icon.outerHTML : icon.innerHTML) : fallback});
    };
    const own = (name, step) => {
      const v = form.querySelector(`[name="${name}"]`).value.trim();
      if (v) items.push({step, text: v.length > 28 ? `${v.slice(0, 26)} …` : v, icon: '✏️'});
    };
    tile('theme', 0); own('theme_custom', 0);
    tile('motif', 1); own('motif_custom', 1);
    tile('message', 2); own('message_custom', 2);
    if (form.querySelector('[name=wishes]').value.trim()) items.push({step: 3, text: 'Wünsche', icon: '✨'});
    tile('style', 4);
    tile('format', 5, '📐');
    if (form.querySelector('[name=foreword]').value.trim()) items.push({step: 7, text: 'Vorwort', icon: '💌'});
    return items;
  }
  function refreshPicks() {
    if (!picks) return;
    // on the summary step the same things are listed big - no pills there
    const items = current === steps.length - 1 ? [] : chosen().filter(i => i.step < current);
    picks.classList.toggle('is-empty', !items.length);
    picks.innerHTML = items.map(i => `<button type="button" class="pick" data-goto="${i.step}"><span class="pick-icon">${i.icon}</span>${escapeHtml(i.text)}</button>`).join('');
  }
  if (picks) picks.addEventListener('click', (e) => {
    const b = e.target.closest('[data-goto]');
    if (b) goTo(Number(b.dataset.goto));
  });
  // jump back to a step: through the history, so the browser's back stays right
  function goTo(step) {
    if (step >= current) return;
    history.go(step - current);
  }

  function summary() {
    const box = form.querySelector('[data-summary]');
    const tiles = chosen().map(i => `<button type="button" class="summary-tile" data-goto="${i.step}"><span class="pick-icon">${i.icon}</span><span class="summary-text">${escapeHtml(i.text)}<small>ändern</small></span></button>`).join('');
    const people = cards.filter(c => !c.hidden).map((card, i) => {
      const name = card.querySelector('input[name$="-name"]').value.trim() || '–';
      const kind = (card.querySelector('[data-kind]:checked') || {value: 'person'}).value;
      const age = card.querySelector('input[name$="-age"]');
      const relation = card.querySelector('[data-relation-input]');
      const preview = card.querySelector('[data-photo-preview]');
      const img = preview && preview.dataset.cropUrl ? {src: preview.dataset.cropUrl} : null;
      const bits = [i === 0 ? '⭐ Hauptfigur' : (relation && relation.value.trim()) || {person: 'Person', animal: 'Tier', object: 'Kuscheltier / Objekt'}[kind]];
      if (kind === 'person' && age && age.value) bits.push(`${age.value} Jahre`);
      return `<button type="button" class="summary-person" data-goto="6">${img ? `<img src="${img.src}" alt="">` : `<span class="summary-noimg">${kind === 'animal' ? '🐾' : kind === 'object' ? '🧸' : '🙂'}</span>`}
        <span><b>${escapeHtml(name)}</b><small>${escapeHtml(bits.join(' · '))}${img ? '' : ' · ohne Foto (geht auch später)'}</small></span></button>`;
    }).join('');
    box.innerHTML = `<div class="summary-grid">${tiles}</div>
      <h3 class="mt-8 text-lg font-semibold">Deine Helden</h3><div class="summary-people mt-3">${people}</div>`;
  }
  form.querySelector('[data-summary]').addEventListener('click', (e) => {
    const b = e.target.closest('[data-goto]');
    if (b) goTo(Number(b.dataset.goto));
  });

  // --- what is bought (last step): the running total, also on the submit button -----------------
  // "Alles erst nach dem Kauf" (Dennis, 2026-10-13): the button leads to the checkout
  const buy = form.querySelector('[data-wizard-buy]');
  function refreshTotal() {
    if (!buy) return;
    let cents = 0;
    buy.querySelectorAll('[data-always]').forEach((el) => { cents += Number(el.dataset.cents); });
    buy.querySelectorAll('input[type=checkbox]:checked').forEach((el) => { cents += Number(el.dataset.cents); });
    const euro = Math.floor(cents / 100) + ',' + String(cents % 100).padStart(2, '0');
    form.querySelectorAll('[data-wizard-total]').forEach((el) => { el.textContent = euro; });
  }
  if (buy) buy.addEventListener('change', () => { refreshTotal(); save(); });

  // --- theme -> motifs ---------------------------------------------------------
  const motifs = [...form.querySelectorAll('[data-motif-of]')];

  function applyTheme() {
    const theme = form.querySelector('[data-theme-choice]:checked');
    // with a theme: its motifs; without one (skipped or own words): all of them
    motifs.forEach(m => {
      const own = !theme || m.dataset.motifOf === theme.value;
      m.hidden = !own;
      if (!own) m.querySelector('input').checked = false;
    });
    // many motifs (Feiertage has 14): smaller tiles, more in a row, so they still fit one screen
    const grid = motifs[0] && motifs[0].parentElement;
    if (grid) grid.classList.toggle('is-dense', motifs.filter(m => !m.hidden).length > 10);
  }

  // --- wishes: idea chips add a sentence -------------------------------------------
  const wishes = form.querySelector('[data-wishes]');
  form.querySelectorAll('[data-wish]').forEach(chip => chip.addEventListener('click', () => {
    wishes.value = (wishes.value.trim() ? wishes.value.trim() + ' ' : '') + chip.dataset.wish;
    wishes.focus();
    wishes.setSelectionRange(wishes.value.length, wishes.value.length);
    refreshNav();
    save();
  }));

  // --- cost estimate (local only) ---------------------------------------------------
  const estimate = form.querySelector('[data-estimate]');
  function refreshEstimate() {
    if (!estimate) return;
    const figures = cards.filter(c => !c.hidden && c.querySelector('input[name$="-name"]').value.trim()).length || 1;
    const pages = 14 + 1;   // every book: 14 story pictures + the cover
    const format = form.querySelector('[data-format]:checked')?.value || 'square';
    const page = format === 'square' ? 0.014 : 0.017;
    const images = figures * 0.03 + pages * page;
    estimate.querySelector('[data-est-figures]').textContent = figures;
    estimate.querySelector('[data-est-pages]').textContent = pages;
    estimate.querySelector('[data-est-page]').textContent = page.toFixed(3);
    estimate.querySelector('[data-est-images]').textContent = images.toFixed(2);
    estimate.querySelector('[data-est-total]').textContent = (images + 0.03).toFixed(2);
  }

  // --- remember everything --------------------------------------------------------
  function save() {
    if (restoring) return;
    const fields = {};
    form.querySelectorAll('input, textarea').forEach(f => {
      // :disabled also covers the fields of a closed (disabled) character card
      if (!f.name || f.type === 'file' || f.type === 'hidden' || f.matches(':disabled')) return;
      if (f.type === 'radio' || f.type === 'checkbox') { if (f.checked) fields[f.name] = f.value; } else if (f.value) fields[f.name] = f.value;
    });
    store({step: current, fields});
    refreshEstimate();
  }

  function restore(fields) {
    Object.entries(fields).forEach(([name, value]) => {
      // a side card with anything typed in it is opened (its name may be the very thing that is missing)
      const match = name.match(/^c(\d)-(name|age|description|notes|relation)$/);
      if (match && Number(match[1]) > 0 && value.trim()) openCard(cards[Number(match[1])]);
      form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach(f => {
        if (f.type === 'radio' || f.type === 'checkbox') f.checked = f.value === value;
        else if (f.type !== 'file' && f.type !== 'hidden') f.value = value;
      });
    });
  }

  // ?motiv=<theme or motif slug> &motiv_text= &botschaft= &stil= &name= &alter= &wunsch= &widmung=
  // -> the highest step a param answered (-1: none, or only name/age)
  const STEP = {theme: 0, motif: 1, motif_custom: 1, message: 2, style: 4};
  function prefill(params) {
    let used = -1;
    const at = (step) => { used = Math.max(used, step); };
    const radio = (name, value) => {
      const input = value && form.querySelector(`input[name="${name}"][value="${CSS.escape(value)}"]`);
      if (input) { input.checked = true; at(STEP[name]); }
      return input;
    };
    const text = (name, value, max) => {
      const field = form.querySelector(`[name="${name}"]`);
      if (field && value && value.trim()) { field.value = value.trim().slice(0, max); if (STEP[name] !== undefined) at(STEP[name]); return true; }
      return false;
    };
    const motiv = params.get('motiv');
    if (motiv) {
      const motif = form.querySelector(`input[name=motif][value="${CSS.escape(motiv)}"]`);
      if (motif) {
        radio('theme', motif.closest('[data-motif-of]').dataset.motifOf);
        motif.checked = true;
        form.querySelector('[name=theme_custom]').value = '';
        form.querySelector('[name=motif_custom]').value = '';
        at(1);
      } else if (radio('theme', motiv)) {          // a theme slug: its motifs come next
        form.querySelector('[name=theme_custom]').value = '';
        form.querySelectorAll('input[name=motif]').forEach(r => { r.checked = false; });
      }
    }
    if (text('motif_custom', params.get('motiv_text'), 300)) form.querySelectorAll('input[name=motif]').forEach(r => { r.checked = false; });
    if (radio('message', params.get('botschaft'))) form.querySelector('[name=message_custom]').value = '';
    radio('style', params.get('stil'));
    text('c0-name', params.get('name'), 60);
    text('dedication', params.get('widmung'), 160);
    const age = params.has('alter') && /^\d{1,3}$/.test(params.get('alter').trim()) ? Number(params.get('alter')) : -1;
    if (age >= 0 && age <= 120) text('c0-age', String(age), 3);
    if (params.get('wunsch')) {
      const wish = form.querySelector('[name=wishes]');
      const add = params.get('wunsch').trim().slice(0, 1000);
      if (add && !wish.value.includes(add)) wish.value = [wish.value.trim(), add].filter(Boolean).join(' ');
      if (add) at(3);
    }
    return used;
  }

  form.addEventListener('input', save);
  form.addEventListener('change', save);

  // the server's answer after an error wins over the stored draft
  const fromServer = JSON.parse(document.getElementById('form-data')?.textContent || '{}');
  const draft = load();
  restoring = true;
  let start = 0;
  if (Object.keys(fromServer).length) {
    restore(fromServer);
    start = Number(form.dataset.errorStep || 0);        // the step the server found a problem in
  } else if (draft && draft.fields) {
    restore(draft.fields);
    start = draft.step || 0;
  }
  // links from the marketing pages (SITE) preselect: they win over the draft, but only for what they set
  // and jump to the first open step after the last one they answered (name/age alone keep the draft's step)
  const touched = Object.keys(fromServer).length ? -1 : prefill(new URLSearchParams(location.search));
  if (touched >= 0) {
    // the format step and later always count as open (their defaults are preselected)
    const next = steps.findIndex((st, i) => i > touched && (!hasAnswer(st) || i >= 5));
    start = next < 0 ? steps.length - 1 : next;
  }
  cards.forEach(applyKind);
  cards.forEach(c => c._markRelation && c._markRelation());
  applyTheme();
  refreshAdd();
  refreshConsent();
  refreshEstimate();
  // the params are used up: a reload goes on from the draft (which now holds them)
  history.replaceState({step: 0}, '', `${location.pathname}#schritt-1`);
  for (let i = 1; i <= start; i++) history.pushState({step: i}, '', `#schritt-${i + 1}`);
  show(start);
  restoring = false;
  save();
}

document.addEventListener('DOMContentLoaded', () => {
  const wizard = document.querySelector('[data-wizard]');
  if (wizard) initWizard(wizard);
});
